import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Receipt, Search, School, X, Upload, MailCheck } from 'lucide-react';
import { ComingSoonButton, Field, PhoneInput, RadioTiles, Stepper, useToast } from '@kollez/design-system';
import { apiGet, apiPost } from '@/lib/api';

/**
 * /register/institution (F81c) — direct port of kollez/pages/public/register-institution.html.
 *   5 steps: Plan · Institution · Colleges (AISHE) · Verification · You → Done timeline
 */

const PLANS = [
  { key: 'pilot', name: 'Free pilot', price: 'Free · one season', badge: 'success' as const, desc: 'One college, one placement season (up to 6 months), 500 final-year students. Continue on any plan without losing anything. Nothing is charged today.' },
  { key: 'college', name: 'College', price: 'Talk to us', desc: 'One campus. Proforma invoice after sign-up; you use the pilot until your PO is processed. Nexie drafts your NIRF, NAAC, AICTE reports.' },
  { key: 'group', name: 'Group', price: 'Talk to us', desc: 'Several colleges as one operation, with a customer success manager. Group roll-up dashboard at 8 am across all campuses.' },
  { key: 'assess', name: 'Assessments only', price: 'Talk to us', desc: 'Exams, question bank and readiness reports, without placements. Six assessment modes, twelve question types.' },
];

const KINDS = [
  { key: 'group', label: 'Education group', sub: 'Several colleges, one trust' },
  { key: 'uni', label: 'University', sub: 'With affiliated colleges' },
  { key: 'college', label: 'Single college', sub: 'One campus' },
];

interface AISHEHit {
  source: 'kollez' | 'aishe';
  aisheCollegeId?: string;
  tenantId?: string;
  name: string;
  city?: string;
  district?: string;
  state?: string;
  breadcrumb?: string;
  tenantKind?: string;
}
interface PickedCollege {
  name: string;
  sub: string;
  source: 'kollez' | 'aishe' | 'manual';
  aisheCollegeId?: string;
  tenantId?: string;
}

export function RegisterInstitutionPage() {
  const [params] = useSearchParams();
  const toast = useToast();
  const [step, setStep] = useState(1);
  const total = 5;

  const [plan, setPlan] = useState<string>(params.get('plan') ?? 'pilot');
  const [kind, setKind] = useState<string>('group');
  const [regName, setRegName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [city, setCity] = useState('');

  // step 3
  const [colleges, setColleges] = useState<PickedCollege[]>([]);
  const [collegeQuery, setCollegeQuery] = useState('');
  const [hits, setHits] = useState<AISHEHit[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // step 4
  const [gstin, setGstin] = useState('');
  const [pan, setPan] = useState('');

  // step 5
  const [firstName, setFirstName] = useState('');
  const [designation, setDesignation] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const [workMobile, setWorkMobile] = useState('');
  const [dpdpAgree, setDpdpAgree] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const gstStatus = useMemo(() => {
    if (!gstin.trim()) return { label: 'Checked against the GST portal', tone: '' };
    const ok = /^\d{2}[A-Z]{5}\d{4}[A-Z]\d[A-Z][A-Z0-9]$/.test(gstin.trim().toUpperCase());
    return ok
      ? { label: 'Valid format · checking with the GST portal…', tone: 'success' as const }
      : { label: 'A GSTIN has 15 characters, e.g. 37AABTS4412K1Z2', tone: 'danger' as const };
  }, [gstin]);

  useEffect(() => {
    const p = params.get('plan'); if (p) setPlan(p);
  }, [params]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = collegeQuery.trim();
    if (q.length < 2) { setHits([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiGet<{ hits: AISHEHit[] }>(
          `/public/tenants/search?q=${encodeURIComponent(q)}`,
        );
        setHits(res.hits ?? []);
      } catch {
        setHits([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [collegeQuery]);

  function pickAisheHit(hit: AISHEHit) {
    if (colleges.some((c) =>
      (hit.aisheCollegeId && c.aisheCollegeId === hit.aisheCollegeId) ||
      (hit.tenantId && c.tenantId === hit.tenantId),
    )) return;
    const sub = hit.source === 'kollez'
      ? `Already on Kollez${hit.breadcrumb ? ` · ${hit.breadcrumb}` : ''}`
      : [hit.city, hit.district, hit.state].filter(Boolean).join(' · ') || 'AISHE master';
    setColleges([...colleges, {
      name: hit.name, sub, source: hit.source,
      aisheCollegeId: hit.aisheCollegeId, tenantId: hit.tenantId,
    }]);
    setCollegeQuery('');
    setHits([]);
  }
  function pickManual(v: string) {
    const name = v.trim();
    if (!name) return;
    if (colleges.some((c) => c.source === 'manual' && c.name.toLowerCase() === name.toLowerCase())) return;
    setColleges([...colleges, { name, sub: 'Manual entry · we verify within a day', source: 'manual' }]);
    setCollegeQuery('');
    setHits([]);
  }
  function removeCollege(i: number) {
    setColleges(colleges.filter((_, idx) => idx !== i));
  }

  async function submitAll() {
    setSubmitting(true);
    try {
      await apiPost('/auth/register/institution', {
        plan, kind, registeredName: regName, displayName, city,
        colleges: colleges.map((c) => ({
          name: c.name,
          source: c.source,
          aisheCollegeId: c.aisheCollegeId,
          tenantId: c.tenantId,
        })),
        verification: { gstin, pan },
        firstAdmin: { name: firstName, designation, workEmail, workMobile },
        dpdpAgree,
      });
      setDone(true);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? 'Could not submit';
      toast.show({ tone: 'warning', message: msg });
    } finally {
      setSubmitting(false);
    }
  }

  function next() {
    if (step < total) setStep(step + 1);
    else submitAll();
  }

  if (done) {
    return (
      <main id="main" className="mx-auto w-full flex-1 px-5 py-8 grid gap-6" style={{ maxWidth: 680 }}>
        <section className="card card-pad grid gap-4 justify-items-start">
          <span className="logo-tile" style={{ width: 52, height: 52, color: 'rgb(var(--ink-success))', borderColor: 'rgb(var(--ink-success))' }}>
            <MailCheck size={22} />
          </span>
          <h1 className="h-page" style={{ fontSize: '2rem' }}>We're checking your details.</h1>
          <p className="subhead">Usually within one working day. We'll email you a sign-in link — you can't sign in until then.</p>
          <ol className="timeline w-full">
            {[
              ['done', 'Submitted', 'Just now'],
              ['current', 'We verify GSTIN, colleges and your authorisation', 'Within 1 working day'],
              ['', 'You invite principals and placement officers', 'Takes about 10 minutes'],
              ['', 'Placement officers import students', 'Then students join with a college code'],
            ].map(([state, title, meta], i) => (
              <li key={i} data-state={state}>
                <span className="t-dot" />
                <div className="t-title">{title}</div>
                <div className="t-meta">{meta}</div>
              </li>
            ))}
          </ol>
          <Link className="btn btn-primary" to="/login">Go to sign in</Link>
        </section>
      </main>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <main id="main" className="mx-auto w-full flex-1 px-5 py-8 grid gap-6 content-start" style={{ maxWidth: 680 }}>
        <Stepper
          current={step}
          steps={[
            { label: 'Plan' },
            { label: 'Institution' },
            { label: 'Colleges' },
            { label: 'Verification' },
            { label: 'You' },
          ]}
        />

        {/* STEP 1 */}
        {step === 1 && (
          <section className="grid gap-4">
            <div>
              <h1 className="h-page">How would you like to start?</h1>
              <p className="subhead mt-2">You can change this before your plan begins. Nothing is charged today.</p>
            </div>
            <RadioTiles
              name="plan"
              value={plan}
              onChange={setPlan}
              ariaLabel="Choose a plan"
              options={PLANS.map((p) => ({
                key: p.key,
                label: p.name,
                sub: p.desc,
                aside: (
                  <span className={p.badge === 'success' ? 'badge badge-success' : 'text-sm'}>
                    {p.price}
                  </span>
                ),
              }))}
            />
            <p className="helper m-0">University with 50+ colleges? <Link className="link" to="/contact?plan=university">Talk to us</Link> — we set those up with you.</p>
          </section>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <section className="grid gap-4">
            <div>
              <h1 className="h-page">Register your institution</h1>
              <p className="subhead mt-2">For education groups, universities and colleges. Students and companies join free once you're set up.</p>
            </div>
            <RadioTiles
              name="kind"
              value={kind}
              onChange={setKind}
              columns={3}
              ariaLabel="Institution kind"
              options={KINDS.map((k) => ({ key: k.key, label: k.label, sub: k.sub }))}
            />
            <div className="card card-pad grid gap-4 sm:grid-cols-2">
              <Field label="Registered name (trust or society)" className="sm:col-span-2">
                <input className="input" placeholder="e.g. Sri Venkateswara Educational Society" value={regName} onChange={(e) => setRegName(e.target.value)} />
              </Field>
              <Field label="Name students will see">
                <input className="input" placeholder="e.g. SV Group of Institutions" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </Field>
              <Field label="City">
                <input className="input" placeholder="Tirupati" value={city} onChange={(e) => setCity(e.target.value)} />
              </Field>
            </div>
          </section>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <section className="grid gap-4">
            <div>
              <h1 className="h-page">Your colleges</h1>
              <p className="subhead mt-2">Search the national AISHE list. We match codes so your NIRF and AICTE data lines up.</p>
            </div>
            <div className="card card-pad grid gap-3">
              <div className="input-group">
                <Search size={14} />
                <input
                  className="input" placeholder="Start typing a college name" value={collegeQuery}
                  onChange={(e) => setCollegeQuery(e.target.value)}
                />
              </div>
              {collegeQuery.trim().length >= 2 && (
                <div className="grid gap-1">
                  {searching && <p className="helper m-0">Searching AISHE…</p>}
                  {!searching && hits.length === 0 && (
                    <p className="helper m-0">
                      No AISHE match.{' '}
                      <button className="link" type="button" onClick={() => pickManual(collegeQuery)}>
                        Add "{collegeQuery.trim()}" manually
                      </button>
                    </p>
                  )}
                  {hits.map((h, i) => (
                    <button
                      key={`${h.source}-${h.aisheCollegeId ?? h.tenantId ?? i}`}
                      type="button"
                      className="well p-2 flex items-center gap-3 text-left hover:bg-bg-hover"
                      onClick={() => pickAisheHit(h)}
                    >
                      <span className="logo-tile"><School size={14} /></span>
                      <div className="flex-1 min-w-0">
                        <div className="cell-title">{h.name}</div>
                        <div className="cell-sub">
                          {h.source === 'kollez'
                            ? `Already on Kollez${h.breadcrumb ? ` · ${h.breadcrumb}` : ''}`
                            : [h.city, h.district, h.state].filter(Boolean).join(' · ') || 'AISHE master'}
                        </div>
                      </div>
                      <span className={`badge ${h.source === 'kollez' ? 'badge-success' : ''}`}>
                        {h.source === 'kollez' ? 'Verified' : 'AISHE'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <div className="grid gap-2">
                {colleges.map((c, i) => (
                  <div key={i} className="well p-3 flex items-center gap-3">
                    <span className="logo-tile"><School size={14} /></span>
                    <div className="flex-1 min-w-0">
                      <div className="cell-title">{c.name}</div>
                      <div className="cell-sub">{c.sub}</div>
                    </div>
                    {c.source === 'kollez' && <span className="badge badge-success">Verified</span>}
                    {c.source === 'manual' && <span className="badge">Manual</span>}
                    <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => removeCollege(i)} aria-label="Remove"><X size={14} /></button>
                  </div>
                ))}
                {colleges.length === 0 && <p className="helper m-0">No colleges added yet.</p>}
                {colleges.length > 0 && <p className="helper m-0">{colleges.length} college{colleges.length === 1 ? '' : 's'} selected</p>}
              </div>
            </div>
          </section>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <section className="grid gap-4">
            <div>
              <h1 className="h-page">Verification</h1>
              <p className="subhead mt-2">We confirm institutions before students' data can be added.</p>
            </div>
            <div className="card card-pad grid gap-4 sm:grid-cols-2">
              <Field
                label={
                  <>
                    GSTIN{' '}
                    <span className="opt text-ink-tertiary text-xs font-normal">
                      {plan === 'pilot' ? '(optional for the pilot)' : '(needed for your invoice)'}
                    </span>
                  </>
                }
                helper={gstStatus.tone === '' ? gstStatus.label : undefined}
                error={gstStatus.tone === 'danger' ? gstStatus.label : undefined}
              >
                <input className="input" placeholder="15 characters" value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} />
                {gstStatus.tone === 'success' && (
                  <span className="helper" style={{ color: 'rgb(var(--ink-success))', marginTop: 4 }}>
                    {gstStatus.label}
                  </span>
                )}
              </Field>
              <Field label="PAN of the trust">
                <input className="input" placeholder="AABTS1234K" value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} />
              </Field>
              <div className="grid gap-1.5 sm:col-span-2">
                <span className="label">
                  Authorisation letter{' '}
                  <span className="opt text-ink-tertiary text-xs font-normal">
                    (on letterhead, signed by the chairman or principal)
                  </span>
                </span>
                <div className="justify-self-start">
                  <ComingSoonButton
                    label="Upload PDF"
                    icon={<Upload size={14} />}
                    reason="Upload flow launches when file storage is wired."
                    variant="outline"
                    showHelper={false}
                  />
                </div>
                <span className="helper">Upload arrives with our verification pipeline. For now, email your letter to <a className="link" href="mailto:verify@kollez.ai">verify@kollez.ai</a>.</span>
              </div>
              <div className="grid gap-1.5 sm:col-span-2">
                <span className="label">
                  Purchase order{' '}
                  <span className="opt text-ink-tertiary text-xs font-normal">
                    (optional now, needed before your first invoice)
                  </span>
                </span>
                <div className="justify-self-start">
                  <ComingSoonButton
                    label="Upload PO"
                    icon={<Upload size={14} />}
                    reason="Upload flow launches when file storage is wired."
                    variant="ghost"
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* STEP 5 */}
        {step === 5 && (
          <section className="grid gap-4">
            <div>
              <h1 className="h-page">You, the first admin</h1>
              <p className="subhead mt-2">You'll invite principals and placement officers after verification.</p>
            </div>
            <div className="card card-pad grid gap-4 sm:grid-cols-2">
              <Field label="Your name">
                <input className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="name" />
              </Field>
              <Field label="Designation">
                <input className="input" placeholder="Director, Placements" value={designation} onChange={(e) => setDesignation(e.target.value)} autoComplete="organization-title" />
              </Field>
              <Field label="Work email" helper="Must be on your institution's domain">
                <input className="input" type="email" placeholder="name@institution.edu.in" value={workEmail} onChange={(e) => setWorkEmail(e.target.value)} autoComplete="email" />
              </Field>
              <Field label="Mobile">
                <PhoneInput value={workMobile} onChange={setWorkMobile} />
              </Field>
              <label className="flex gap-2 items-start text-sm sm:col-span-2">
                <input type="checkbox" className="check mt-0.5" checked={dpdpAgree} onChange={(e) => setDpdpAgree(e.target.checked)} />
                <span>I agree to the <Link className="link" to="/security">data processing terms</Link> on behalf of the institution (DPDP Act, 2023).</span>
              </label>
            </div>
          </section>
        )}

        <div className="wizard-foot" style={{ borderRadius: 10 }}>
          <button className="btn btn-ghost" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}>Back</button>
          <span className="helper ml-auto">Step {step} of {total}</span>
          <button className="btn btn-primary" onClick={next} disabled={submitting || (step === 5 && !dpdpAgree)}>
            {step === total ? (submitting ? 'Submitting…' : 'Submit for verification') : 'Continue'}
          </button>
        </div>
      </main>
    </div>
  );
}
